// Moving the Author to what a blocked action wants from them. Highlighting
// alone still leaves the missing field off-screen on a long form; focus scrolls
// it into view, and a screen reader follows the focus for free.
import { useCallback, useEffect, useState } from "react";

/**
 * Scrolls to and focuses the first thing worth looking at: an invalid control,
 * or any element the screen marked with `data-guidance-target` (a card to read,
 * a checkbox to tick). Focus lands only when something is found, so the caller
 * does not have to know whether the problem is a field or a paragraph.
 */
export function guideAttention(scope?: ParentNode): boolean {
  const root = scope ?? document;
  const target = root.querySelector<HTMLElement>("[aria-invalid='true'], [data-guidance-target]");
  if (!target) return false;
  target.scrollIntoView({ block: "center", behavior: "smooth" });
  const focusable =
    target.matches("input, select, textarea, button, a[href]") || target.tabIndex >= 0
      ? target
      : (target.querySelector<HTMLElement>("input, select, textarea, button, a[href]") ?? target);
  focusable.focus({ preventScroll: true });
  return true;
}

/**
 * The screen's side of a gated action. `proceed` is what a blocked button
 * calls: it marks the form as attempted (fields show their error highlight
 * from then on, not while the Author is still typing) and, after those errors
 * have painted, moves focus to the first invalid or marked element. Every
 * further blocked click guides again, so fixing the first problem and clicking
 * once more moves the Author to the next one.
 */
export function useGuidance(): {
  readonly attempted: boolean;
  readonly proceed: () => void;
} {
  const [attempts, setAttempts] = useState(0);
  useEffect(() => {
    if (attempts > 0) guideAttention();
  }, [attempts]);
  const proceed = useCallback(() => setAttempts((n) => n + 1), []);
  return { attempted: attempts > 0, proceed };
}
